import React from 'react';
import { Check } from 'lucide-react';
import { Id } from '@hero/schema';
import { cn } from '@hero/web-lib';
import type { OptionDisplay, SelectAsyncOptionDisplayStyle } from '@hero/web-hooks';
import { PencilIcon } from '@hero/web-icons';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../../ui/Checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/Tooltip';
import { isOptionDisplay } from './constants';

export interface SelectAsyncOptionRowColorClasses {
  avatarBg: string;
  avatarText: string;
  dot: string;
}

export interface SelectAsyncOptionRowProps<T extends { _id: Id }> {
  item: T;
  isSelected: boolean;
  mode: 'single' | 'multi';
  getItemId: (item: T) => Id;
  getDisplayFromItem: (item: T) => { label: string; desc?: string; subDesc?: string };
  optionDisplayStyle?: SelectAsyncOptionDisplayStyle | null;
  colorClasses: SelectAsyncOptionRowColorClasses | null;
  showAvatar: boolean;
  renderOption?: (item: T, context: { isSelected: boolean }) => React.ReactNode | OptionDisplay;
  /** @deprecated No longer needed — RTL is handled via Tailwind rtl: variants */
  isRTL?: boolean;
  allowEdit?: boolean;
  onEdit?: (item: T) => void;
}

export function SelectAsyncOptionRow<T extends { _id: Id }>({
  item,
  isSelected: isSelectedFlag,
  mode,
  getItemId,
  getDisplayFromItem,
  optionDisplayStyle,
  colorClasses,
  showAvatar,
  renderOption,
  allowEdit = false,
  onEdit,
}: SelectAsyncOptionRowProps<T>) {
  const { t } = useTranslation(['c-main']);
  const editButton = allowEdit && onEdit && (
    <TooltipProvider delayDuration={0}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              onEdit(item);
            }}
            className={cn(
              'flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity',
              'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
              'ms-1',
            )}
            aria-label={t('c-main:crud_buttons.edit')}>
            <PencilIcon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="left"
          sideOffset={6}
          className="z-[100] px-2 py-1 text-xs font-medium">
          {t('c-main:crud_buttons.edit')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const useBuiltInDisplay = optionDisplayStyle != null && !renderOption && colorClasses != null;

  function defaultRenderOption() {
    const display = getDisplayFromItem(item);
    return (
      <div className="flex items-center gap-2 w-full group rtl:flex-row-reverse">
        {mode === 'multi' && (
          <Checkbox
            checked={isSelectedFlag}
            className="flex-shrink-0 pointer-events-none"
            aria-hidden
          />
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0 rtl:flex-row-reverse">
          <div className="flex-1 min-w-0 text-start">
            <div className="font-medium text-sm truncate">{display.label}</div>
            {display.desc != null && display.desc !== '' && (
              <div className="text-xs text-gray-500 truncate">{display.desc}</div>
            )}
          </div>
        </div>
        {mode === 'single' && (
          <Check
            className={cn(
              'h-4 w-4 flex-shrink-0 ms-auto',
              isSelectedFlag ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}
        {editButton}
      </div>
    );
  }

  function renderBuiltInOptionRow() {
    if (!colorClasses) return defaultRenderOption();
    const display = getDisplayFromItem(item);
    const initial = display.label.charAt(0).toUpperCase();
    return (
      <div className="flex items-center gap-3 w-full group rtl:flex-row-reverse">
        {mode === 'multi' && (
          <Checkbox
            checked={isSelectedFlag}
            className="flex-shrink-0 pointer-events-none"
            aria-hidden
          />
        )}
        {showAvatar && (
          <div
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
              colorClasses.avatarBg,
              colorClasses.avatarText,
              'font-semibold text-sm',
            )}>
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0 text-start">
          <div className="font-medium text-sm truncate">{display.label}</div>
          {display.desc != null && display.desc !== '' && (
            <div className="text-xs text-gray-500 truncate">{display.desc}</div>
          )}
          {display.subDesc != null && display.subDesc !== '' && (
            <div className="text-xs text-gray-400 truncate">{display.subDesc}</div>
          )}
        </div>
        {mode === 'single' && isSelectedFlag && (
          <div className={cn('w-2 h-2 rounded-full flex-shrink-0', colorClasses.dot)} aria-hidden />
        )}
        {editButton}
      </div>
    );
  }

  if (renderOption) {
    const result = renderOption(item, { isSelected: isSelectedFlag });
    if (isOptionDisplay(result)) {
      return (
        <div className="flex items-center gap-2 w-full group rtl:flex-row-reverse">
          {mode === 'multi' && (
            <Checkbox
              checked={isSelectedFlag}
              className="flex-shrink-0 pointer-events-none"
              aria-hidden
            />
          )}
          <div className="flex items-center gap-2 flex-1 min-w-0 rtl:flex-row-reverse">
            {result.icon && <div className="flex-shrink-0">{result.icon}</div>}
            <div className="flex-1 min-w-0 text-start">
              <div className="font-medium text-sm truncate">{result.label}</div>
              {result.desc && <div className="text-xs text-gray-500 truncate">{result.desc}</div>}
              {result.subDesc && (
                <div className="text-xs text-gray-400 truncate">{result.subDesc}</div>
              )}
            </div>
          </div>
          {mode === 'single' && (
            <Check
              className={cn(
                'h-4 w-4 flex-shrink-0 ms-auto',
                isSelectedFlag ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
          {editButton}
        </div>
      );
    }
    return <>{result}</>;
  }
  if (useBuiltInDisplay) return renderBuiltInOptionRow();
  return defaultRenderOption();
}
